import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { getClaimDocuments, uploadDocumentToFolder, downloadClaimDocument } from '../services/claimsApi'
import { getApiErrorMessage, getAttachmentActionErrorMessage } from '../utils/errorMessages'
import { useSsoToken } from './useSsoToken'
import type { Attachment } from '../types/attachment'
import type { DocumentInfo } from '@api-sdk/models/DocumentInfo'

/* global Office */

const MAX_FILE_SIZE_MB = 25
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024

export type AttachmentMode = 'add' | 'save'

export interface AttachmentProgress {
  current: number
  total: number
}

interface ClaimAttachmentsState {
  tree: DocumentInfo[]
  navigationStack: DocumentInfo[]
  emailAttachments: Attachment[]
  loading: boolean
  error: string | null
  selectedIds: string[]
  savedIds: string[]
  savedFileNames: string[]
  progress: AttachmentProgress | null
  actionError: string | null
  successMessage: string | null
}

export interface UseClaimAttachmentsResult {
  attachments: Attachment[]
  emailAttachments: Attachment[]
  boxAttachments: Attachment[]
  loading: boolean
  error: string | null
  selectedIds: string[]
  progress: AttachmentProgress | null
  actionError: string | null
  successMessage: string | null
  canGoBack: boolean
  canExecuteAction: boolean
  currentFolderName: string
  navigationPath: string[]
  toggleSelect: (id: string) => void
  toggleSelectAll: () => void
  navigateTo: (id: string) => void
  navigateBack: () => void
  executeAction: () => Promise<void>
  cancelAction: () => void
}

function mapDocumentToAttachment(doc: DocumentInfo, selectable = false): Attachment {
  return {
    id: doc.id,
    name: doc.name,
    type: doc.isFolder ? 'folder' : 'file',
    source: 'box',
    selectable: !doc.isFolder && selectable,
    extension: doc.extension ?? undefined,
    size: doc.size == null ? undefined : String(Number(doc.size)),
    modifiedAt: doc.createdOn ? new Date(doc.createdOn).toLocaleDateString() : undefined,
  }
}

function getCurrentFolderItems(tree: DocumentInfo[], navigationStack: DocumentInfo[]): DocumentInfo[] {
  if (navigationStack.length === 0) return tree
  return navigationStack[navigationStack.length - 1].children ?? []
}

function mapEmailAttachmentToAttachment(item: Office.AttachmentDetails): Attachment {
  return {
    id: item.id,
    name: item.name,
    type: 'file',
    source: 'email',
    selectable: true,
    size: item.size == null ? undefined : String(item.size),
  }
}

async function getEmailAttachments(): Promise<Attachment[]> {
  const item = Office?.context?.mailbox?.item
  if (!item) return []
  const attachments = (item as Office.MessageRead).attachments ?? []
  return attachments.map(mapEmailAttachmentToAttachment)
}

async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve((reader.result as string).split(',')[1])
    reader.onerror = () => reject(new Error('Failed to read file'))
    reader.readAsDataURL(blob)
  })
}

async function addFileToEmail(id: string, name: string, token: string): Promise<void> {
  const blob = await downloadClaimDocument(id, token)
  const base64 = await blobToBase64(blob)
  return new Promise((resolve, reject) => {
    const item = Office.context.mailbox.item as Office.MessageCompose
    item.addFileAttachmentFromBase64Async(base64, name, (result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve()
      } else {
        reject(new Error(result.error?.message || `Failed to attach ${name}`))
      }
    })
  })
}

async function getEmailAttachmentContent(id: string): Promise<File> {
  return new Promise((resolve, reject) => {
    const item = Office.context.mailbox.item as Office.MessageRead
    item.getAttachmentContentAsync(id, (result) => {
      if (result.status !== Office.AsyncResultStatus.Succeeded) {
        reject(new Error(result.error?.message || 'Failed to get attachment content'))
        return
      }
      const content = result.value
      const attachment = (item.attachments ?? []).find((a) => a.id === id)
      const fileName = attachment?.name ?? id

      if (content.format === Office.MailboxEnums.AttachmentContentFormat.Base64) {
        const binary = atob(content.content)
        const bytes = new Uint8Array(binary.length)
        for (let i = 0; i < binary.length; i++) {
          bytes[i] = binary.codePointAt(i) ?? 0
        }
        resolve(new File([bytes], fileName))
      } else {
        resolve(new File([content.content], fileName))
      }
    })
  })
}

function getUniqueFilename(name: string, existingNames: Set<string>): string {
  if (!existingNames.has(name)) return name
  const dotIndex = name.lastIndexOf('.')
  const base = dotIndex > 0 ? name.slice(0, dotIndex) : name
  const ext = dotIndex > 0 ? name.slice(dotIndex) : ''
  let counter = 1
  while (existingNames.has(`${base} (${counter})${ext}`)) {
    counter++
  }
  return `${base} (${counter})${ext}`
}

interface FileError {
  reason: string
  fileName: string
}

async function processItems<T extends Attachment>(
  ids: string[],
  attachments: T[],
  signal: AbortSignal,
  onProgress: () => void,
  processOne: (id: string, item: T) => Promise<{ savedId: string; savedName: string } | { error: FileError }>
): Promise<{ errors: FileError[]; savedIds: string[]; savedNames: string[] }> {
  const errors: FileError[] = []
  const savedIds: string[] = []
  const savedNames: string[] = []
  for (const id of ids) {
    if (signal.aborted) break
    const item = attachments.find((a) => a.id === id)
    if (!item) continue
    const result = await processOne(id, item)
    if ('error' in result) {
      errors.push(result.error)
    } else {
      savedIds.push(result.savedId)
      savedNames.push(result.savedName)
    }
    onProgress()
  }
  return { errors, savedIds, savedNames }
}

function joinFileErrors(errors: FileError[]): string | null {
  if (errors.length === 0) return null
  const byReason = new Map<string, string[]>()
  for (const { reason, fileName } of errors) {
    const names = byReason.get(reason) ?? []
    names.push(fileName)
    byReason.set(reason, names)
  }
  return [...byReason.entries()].map(([reason, names]) => `${reason}: ${names.join(', ')}`).join(' • ')
}

function fileSizeError(name: string, size: string | undefined): FileError | null {
  if (size !== undefined && Number(size) > MAX_FILE_SIZE_BYTES) {
    return { reason: `Exceeds the ${MAX_FILE_SIZE_MB} MB file size limit`, fileName: name }
  }
  return null
}

async function processAddItems(
  ids: string[],
  attachments: Attachment[],
  token: string,
  signal: AbortSignal,
  onProgress: () => void
): Promise<{ errors: FileError[]; savedIds: string[]; savedNames: string[] }> {
  return processItems(ids, attachments, signal, onProgress, async (id, doc) => {
    const sizeErr = fileSizeError(doc.name, doc.size)
    if (sizeErr) return { error: sizeErr }
    try {
      await addFileToEmail(id, doc.name, token)
      return { savedId: id, savedName: doc.name }
    } catch (err) {
      return { error: { reason: getAttachmentActionErrorMessage(err, 'Failed to attach'), fileName: doc.name } }
    }
  })
}

async function processSaveItems(
  ids: string[],
  emailAttachments: Attachment[],
  targetFolderId: string,
  token: string,
  existingNames: Set<string>,
  signal: AbortSignal,
  onProgress: () => void
): Promise<{ errors: FileError[]; savedIds: string[]; savedNames: string[] }> {
  return processItems(ids, emailAttachments, signal, onProgress, async (id, attachment) => {
    const sizeErr = fileSizeError(attachment.name, attachment.size)
    if (sizeErr) return { error: sizeErr }
    try {
      const file = await getEmailAttachmentContent(id)
      const uniqueName = getUniqueFilename(file.name, existingNames)
      const uploadFile = uniqueName === file.name ? file : new File([file], uniqueName, { type: file.type })
      existingNames.add(uniqueName)
      await uploadDocumentToFolder(targetFolderId, uploadFile, token, signal)
      return { savedId: id, savedName: uniqueName }
    } catch (err) {
      return { error: { reason: getAttachmentActionErrorMessage(err, 'Failed to save'), fileName: attachment.name } }
    }
  })
}

export function useClaimAttachments(
  claimNumber: string,
  folderId: string | null | undefined,
  mode: AttachmentMode,
  enabled: boolean
): UseClaimAttachmentsResult {
  const { getToken } = useSsoToken()
  const abortControllerRef = useRef<AbortController | null>(null)
  const [state, setState] = useState<ClaimAttachmentsState>({
    tree: [],
    navigationStack: [],
    emailAttachments: [],
    loading: true,
    error: null,
    selectedIds: [],
    savedIds: [],
    savedFileNames: [],
    progress: null,
    actionError: null,
    successMessage: null,
  })

  useEffect(() => {
    if (!enabled) return

    async function load() {
      setState((prev) => ({ ...prev, actionError: null, successMessage: null, selectedIds: [], savedIds: [], savedFileNames: [] }))

      if (!folderId) return

      try {
        const token = await getToken()
        if (mode === 'save') {
          const [docs, emailItems] = await Promise.all([getClaimDocuments(folderId, token), getEmailAttachments()])
          setState((prev) => ({
            ...prev,
            tree: docs,
            emailAttachments: emailItems,
            navigationStack: [],
            loading: false,
          }))
        } else {
          const docs = await getClaimDocuments(folderId, token)
          setState((prev) => ({ ...prev, tree: docs, navigationStack: [], loading: false }))
        }
      } catch (err) {
        console.error('[useClaimAttachments] Failed to load:', err)
        const errorMessage = getApiErrorMessage(err, 'Failed to load attachments. Please try again.')
        setState((prev) => ({ ...prev, loading: false, error: errorMessage }))
      }
    }

    void load()
  }, [enabled, folderId, mode, getToken])

  const boxAttachments = useMemo(
    () =>
      getCurrentFolderItems(state.tree, state.navigationStack).map((doc) =>
        mapDocumentToAttachment(doc, mode === 'add' && !state.savedIds.includes(doc.id))
      ),
    [mode, state.tree, state.navigationStack, state.savedIds]
  )

  const emailAttachments = useMemo(() => {
    if (mode !== 'save') return []
    return state.emailAttachments.map((a) => ({
      ...a,
      selectable: state.savedIds.includes(a.id) ? false : a.selectable,
    }))
  }, [mode, state.emailAttachments, state.savedIds])

  const attachments = useMemo(
    () => (mode === 'save' ? [...emailAttachments, ...boxAttachments] : boxAttachments),
    [mode, emailAttachments, boxAttachments]
  )

  const canGoBack = state.navigationStack.length > 0
  const currentFolderName = canGoBack ? state.navigationStack[state.navigationStack.length - 1].name : 'Attachments'
  const navigationPath = state.navigationStack.map((d) => d.name)
  const currentFolderId = canGoBack ? state.navigationStack[state.navigationStack.length - 1].id : (folderId ?? null)
  const currentFolderItemNames = useMemo(
    () => new Set(getCurrentFolderItems(state.tree, state.navigationStack).map((d) => d.name)),
    [state.tree, state.navigationStack]
  )

  const toggleSelect = useCallback((id: string) => {
    setState((prev) => ({
      ...prev,
      selectedIds: prev.selectedIds.includes(id) ? prev.selectedIds.filter((x) => x !== id) : [...prev.selectedIds, id],
    }))
  }, [])

  const toggleSelectAll = useCallback(() => {
    setState((prev) => {
      const selectableIds =
        mode === 'save'
          ? prev.emailAttachments.filter((a) => !prev.savedIds.includes(a.id)).map((a) => a.id)
          : getCurrentFolderItems(prev.tree, prev.navigationStack)
              .filter((doc) => !doc.isFolder && !prev.savedIds.includes(doc.id))
              .map((doc) => doc.id)
      const allSelected = selectableIds.length > 0 && selectableIds.every((id) => prev.selectedIds.includes(id))
      return { ...prev, selectedIds: allSelected ? [] : selectableIds }
    })
  }, [mode])

  const navigateTo = useCallback((id: string) => {
    setState((prev) => {
      const currentItems = getCurrentFolderItems(prev.tree, prev.navigationStack)
      const item = currentItems.find((d) => d.id === id)
      if (!item?.isFolder) return prev
      return { ...prev, navigationStack: [...prev.navigationStack, item], selectedIds: [] }
    })
  }, [])

  const navigateBack = useCallback(() => {
    setState((prev) => ({ ...prev, navigationStack: prev.navigationStack.slice(0, -1), selectedIds: [] }))
  }, [])

  const canExecuteAction = state.selectedIds.length > 0

  const cancelAction = useCallback(() => {
    abortControllerRef.current?.abort()
  }, [])

  const executeAction = useCallback(async () => {
    if (state.selectedIds.length === 0) return

    const controller = new AbortController()
    abortControllerRef.current = controller

    setState((prev) => ({
      ...prev,
      actionError: null,
      successMessage: null,
      progress: { current: 0, total: prev.selectedIds.length },
    }))

    const onProgress = () =>
      setState((prev) =>
        prev.progress ? { ...prev, progress: { ...prev.progress, current: prev.progress.current + 1 } } : prev
      )

    const token = await getToken()
    let savedIds: string[]
    let savedNames: string[]
    let errors: FileError[]

    if (mode === 'add') {
      const result = await processAddItems(state.selectedIds, attachments, token, controller.signal, onProgress)
      savedIds = result.savedIds
      savedNames = result.savedNames
      errors = result.errors
    } else {
      let existingNames = new Set([...currentFolderItemNames, ...state.savedFileNames])
      if (currentFolderId) {
        try {
          const freshItems = await getClaimDocuments(currentFolderId, token)
          existingNames = new Set(freshItems.map((d) => d.name))
        } catch {
          // fall back to cached + session-saved names
        }
      }
      const result = await processSaveItems(
        state.selectedIds,
        state.emailAttachments,
        currentFolderId!,
        token,
        existingNames,
        controller.signal,
        onProgress
      )
      savedIds = result.savedIds
      savedNames = result.savedNames
      errors = result.errors
    }

    const actionLabel = mode === 'add' ? 'Added to email' : 'Saved'
    const successMessage = savedNames.length > 0 ? `${actionLabel}: ${savedNames.join(', ')}` : null
    const joinedError = joinFileErrors(errors)

    setState((prev) => ({
      ...prev,
      progress: null,
      selectedIds: controller.signal.aborted ? prev.selectedIds.filter((id) => !savedIds.includes(id)) : [],
      savedIds: [...prev.savedIds, ...savedIds],
      savedFileNames: [...prev.savedFileNames, ...savedNames],
      successMessage: controller.signal.aborted ? null : successMessage,
      actionError: controller.signal.aborted ? null : joinedError,
    }))

    if (mode === 'save' && folderId) {
      try {
        const refreshedDocs = await getClaimDocuments(folderId, token)
        setState((prev) => ({ ...prev, tree: refreshedDocs }))
      } catch {
        // refresh failure is non-critical — list will update on next panel open
      }
    }
  }, [
    state.selectedIds,
    state.emailAttachments,
    state.savedFileNames,
    attachments,
    mode,
    currentFolderId,
    currentFolderItemNames,
    folderId,
    getToken,
  ])

  return {
    attachments,
    emailAttachments,
    boxAttachments,
    loading: state.loading,
    error: state.error,
    selectedIds: state.selectedIds,
    progress: state.progress,
    actionError: state.actionError,
    successMessage: state.successMessage,
    canGoBack,
    canExecuteAction,
    currentFolderName,
    navigationPath,
    toggleSelect,
    toggleSelectAll,
    navigateTo,
    navigateBack,
    executeAction,
    cancelAction,
  }
}
